import {
  AccordionCard,
  Button,
  Title,
  TitleWithUnderLine
} from '@boi/core/lib';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';

import { CognitoContext } from '../../../CognitoUtils';
import {
  commonPageStylesIdentifier,
  HOME_QUOTE_THANK_YOU_STORAGE
} from '../../../constants';
import {
  NO_PREPOPULATION,
  PREPOPULATION_PREFORMED,
  QUOTE_REFERENCE
} from '../../../constants/home';
import { HOME_INSURANCE_TYPE } from '../../../constants/insuranceTypeConstants';
import { JOURNEY_ABOUT_YOU } from '../../../constants/journey';
import {
  GET_QUOTE_VALUES_STORAGE,
  PAYMENT_SUCCESS,
  POLICY_RENEWAL,
  SUBMITTED_GET_QUOTE_STORAGE
} from '../../../constants/sessionStorage/genericStorageIdentifiers';
import { HOME_LATEST_QUOTE_STORAGE } from '../../../constants/sessionStorage/homeStorageIdentifiers';
import {
  AGENT_CLICKED_REFERENCE_QUOTES,
  AGENT_SELECTED_QUOTE,
  BOI_WALLET_TOWN_COUNTY_DATA,
  BOI_WALLET_USER_NAME_STORAGE
} from '../../../constants/sessionStorage/userStorageIdentifiers';
import {
  commonClass,
  getItemFromSessionStorage,
  getObjectFromSessionStorage,
  isAgent,
  removeFromSessionStorage,
  saveInSessionStorage
} from '../../../helpers';
import {
  saveDataInLatestQuote,
  scrollToFirstError
} from '../../../helpers/HomeCommonMethods';
import { aboutYouObjInitialValues } from '../../../pages/GetQuotePage/InitalValues';
import useGetQuotePageValidity from '../../../pages/GetQuotePage/useGetQuotePageValidity';
import useSectionOpenedStatus from '../../../pages/GetQuotePage/useSectionOpenedStatus';
import { routes } from '../../../routes';
import { getTownAndCounty } from '../../../services/homeServices';
import UserContext from '../../../UserContext';
import LegalText from '../../LegalText';
import { MobileBackButton } from '../../MobileBackButton';
import ProgressBar from '../../ProgressBar';
import FormikAboutYouForm from './AboutYouForm';
import { AboutYouBanner } from './AboutYouFormChildComponents/AboutYouBanner';

const className = 'c-GetQuotePage';

const AboutYouComponent = ({ latestQuote, history }) => {
  const [prepopulationStatus, setPrepopulationStatus] = useState(
    NO_PREPOPULATION
  );

  const [aboutYouObj, setAboutYou] = useState(
    Object.assign({}, aboutYouObjInitialValues)
  );

  const [opened, handleFormOpen] = useSectionOpenedStatus();
  const [aboutYouFormTrackCount, setaboutYouFormTrackCount] = useState(0);
  const [triggerSubmitButton, setTriggerSubmitButton] = useState(false);
  const [townAndCountyData, setTownsAndCountyData] = useState([]);
  const [validity, handleFormSubmit] = useGetQuotePageValidity();
  const { userDispatch } = useContext(UserContext);
  const { cognitoUser } = useContext(CognitoContext);

  let emailParam;

  if (isAgent() && getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES)) {
    emailParam = getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES);
  }

  const updateFirstName = value => {
    userDispatch({
      type: 'setFirstName',
      payload: { firstName: value }
    });
    saveInSessionStorage(BOI_WALLET_USER_NAME_STORAGE, value);
  };
  const setAboutYouObjForMultipleUpdatedFields = updatedFields => {
    const newValues = { ...aboutYouObj, ...updatedFields };
    if (updatedFields.firstName !== undefined)
      updateFirstName(updatedFields.firstName.toString());
    setAboutYou(newValues);
  };

  const setAboutYouObjForUpdatedFields = (fieldName, value) => {
    const newValues = { ...aboutYouObj };
    newValues[fieldName] = value;
    if (fieldName === 'firstName') updateFirstName(value.toString());
    setAboutYou(newValues);
  };

  const setUpdatedAboutYouValues = async values => {
    setAboutYou(values);
    updateFirstName(values.firstName);
  };

  const loadAboutYou = (object, override) => {
    const replaceOrKeepValue = key =>
      key in object && object[key] !== '' ? object[key] : aboutYouObj[key];
    setAboutYou(
      Object.keys(aboutYouObj).reduce((newObj, key) => {
        newObj[key] = override ? object[key] : replaceOrKeepValue(key);
        return newObj;
      }, {})
    );
  };

  const loadAgentFormValues = forms => {
    saveInSessionStorage(HOME_QUOTE_THANK_YOU_STORAGE, JSON.stringify(forms));
    if (forms.aboutYou) loadAboutYou(forms.aboutYou, true);
    setPrepopulationStatus(PREPOPULATION_PREFORMED);
  };

  const personaliseUsingLatestQuoteFirstName = () => {
    if (latestQuote.aboutYou && latestQuote.aboutYou.firstName) {
      updateFirstName(latestQuote.aboutYou.firstName);
    }
  };

  const setTownsAndCounty = async () => {
    try {
      const townAndCountyData = await getTownAndCounty();
      saveInSessionStorage(
        BOI_WALLET_TOWN_COUNTY_DATA,
        JSON.stringify(townAndCountyData)
      );
      setTownsAndCountyData(townAndCountyData);
    } catch (e) {
      saveInSessionStorage(BOI_WALLET_TOWN_COUNTY_DATA, []);
      setTownsAndCountyData([]);
    }
  };

  const renderSubmitButton = () => (
    <div className={`${className}__button`}>
      <a
        data-ga
        id={`GetQuote__getCoveredButton`}
        data-testid={`GetQuote__getCoveredButton`}
      >
        <Button
          quaternary
          fluid
          onClick={() => {
            setaboutYouFormTrackCount(aboutYouFormTrackCount + 1);
            setTriggerSubmitButton(true);
            if (validity.aboutYou) {
              goToYourHomePage();
            } else setTimeout(scrollToFirstError, 1);
          }}
        >
          Next
        </Button>
      </a>
    </div>
  );

  // eslint-disable-next-line complexity
  const setHomeThankYouStorage = isLatest => {
    const homeQuote = getObjectFromSessionStorage(
      `${HOME_QUOTE_THANK_YOU_STORAGE}`
    );
    return {
      aboutYouObj: isLatest ? latestQuote.aboutYou : isAgent() ? aboutYouObj : homeQuote?.aboutYouObj,
      yourHomeObj: homeQuote?.yourHomeObj || homeQuote?.yourHome || null,
      yourCoverObj: homeQuote?.yourCoverObj || homeQuote?.yourCover || null,
      assumptions: homeQuote?.assumptions || homeQuote?.assumptions || null,
      extraQuestionsValues:
        homeQuote?.extraQuestionsValues || homeQuote?.assumptions || null
    };
  };

  const isQuoteDataDifferent = (sessionStorageQuote, quoteData) => {
    const { aboutYou: sessionAboutYou } = sessionStorageQuote;
    const { aboutYouObj: currentAboutYou } = quoteData;
    if (!sessionAboutYou) return;
    
    return (
      sessionAboutYou?.firstName !== currentAboutYou?.firstName ||
      sessionAboutYou?.lastName !== currentAboutYou?.lastName ||
      sessionAboutYou?.ecad_identifier !== currentAboutYou?.ecad_identifier
    );
  };

  const isSessionStorageQuoteEmpty = sessionStorageQuote => {
    return Object.keys(sessionStorageQuote).length === 0;
  };

  const checkAndRemoveQuoteReference = quoteData => {
    const sessionStorageQuote = getObjectFromSessionStorage(
      `${HOME_QUOTE_THANK_YOU_STORAGE}`
    );

    if (isSessionStorageQuoteEmpty(sessionStorageQuote)) return;

    if (
      isQuoteDataDifferent(
        sessionStorageQuote.aboutYou ? sessionStorageQuote : latestQuote,
        quoteData
      )
    ) {
      removeFromSessionStorage(`${QUOTE_REFERENCE}`);
    }
  };

  // eslint-disable-next-line complexity
  const goToYourHomePage = () => {
    const linkAddress = `${routes.getQuotePage.url}yourHome`;
    const quoteData = setHomeThankYouStorage(false);

    checkAndRemoveQuoteReference(quoteData);

    saveInSessionStorage(
      HOME_QUOTE_THANK_YOU_STORAGE,
      JSON.stringify(quoteData)
    );
    const sessionStorageLatestQuote = getObjectFromSessionStorage(
      `${HOME_LATEST_QUOTE_STORAGE}`
    );
    if (Object.keys(sessionStorageLatestQuote).length !== 0) {
      saveDataInLatestQuote(
        sessionStorageLatestQuote,
        'aboutYou',
        quoteData.aboutYouObj
      );
    }
    if (
      isAgent() &&
      getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES)
    ) {
      removeFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES);
    }
    history.push({
      pathname: linkAddress,
      state: quoteData
    });
  };

  const getFormStatus = formName => {
    if (opened[formName]) {
      return validity[formName] ? 'success' : 'warning';
    }
    return 'default';
  };

  const sessionStoredValues = getObjectFromSessionStorage(
    `${HOME_INSURANCE_TYPE}${GET_QUOTE_VALUES_STORAGE}`
  );

  // eslint-disable-next-line complexity
  const preloadForm = () => {
    //load towns and counties
    const flag = getObjectFromSessionStorage(BOI_WALLET_TOWN_COUNTY_DATA);
    if (flag === null || Object.keys(flag).length === 0) {
      setTownsAndCounty();
    }

    //object maker for copying new properties from the newObj to the oldObj
    const objMaker = (oldObj, newObj) =>
      Object.assign({}, oldObj, newObj || {});

    //const to make sure object is not null or undefined
    const hasValues = obj =>
      typeof obj === 'object' && Object.keys(obj).length > 0;

    const submitted = getItemFromSessionStorage(
      `${HOME_INSURANCE_TYPE}${SUBMITTED_GET_QUOTE_STORAGE}`
    );
    const values = getObjectFromSessionStorage(
      `${HOME_QUOTE_THANK_YOU_STORAGE}`
    );

    //checks submitted is false or undefined and that the values const has keys (variables)
    const unSubmitted = () => !submitted && hasValues(values);

    const submittedLatest = () =>
      hasValues(latestQuote) &&
      (!isAgent() || getObjectFromSessionStorage(AGENT_SELECTED_QUOTE)) &&
      !hasValues(values);

    const submittedSession = () => hasValues(sessionStoredValues) && !isAgent();

    if (unSubmitted() && !submittedLatest()) {
      setAboutYou(oldAboutYou =>
        objMaker(oldAboutYou, values.aboutYouObj || latestQuote.aboutYou)
      );
      setPrepopulationStatus(PREPOPULATION_PREFORMED);
    } else if (submittedLatest()) {
      setAboutYou(oldAboutYou => objMaker(oldAboutYou, latestQuote.aboutYou));
      const quoteData = setHomeThankYouStorage(true);
      saveInSessionStorage(
        HOME_QUOTE_THANK_YOU_STORAGE,
        JSON.stringify(quoteData)
      );
      setPrepopulationStatus(PREPOPULATION_PREFORMED);
    } else if (submittedSession()) {
      loadAboutYou(sessionStoredValues, false);
      setPrepopulationStatus(PREPOPULATION_PREFORMED);
    }
  };

  useEffect(() => {
    setTownsAndCounty();
    removeFromSessionStorage(POLICY_RENEWAL);
    removeFromSessionStorage(PAYMENT_SUCCESS);

    const storedQuoteDetails = getObjectFromSessionStorage(HOME_QUOTE_THANK_YOU_STORAGE);
    if (storedQuoteDetails?.aboutYouObj && isAgent()) {
      const aboutYouMapped = Object.assign({}, aboutYouObj, storedQuoteDetails.aboutYouObj);
      setAboutYou(aboutYouMapped);
    }
  }, []);

  useEffect(() => {
    preloadForm();
  }, [latestQuote]);

  useEffect(() => {
    personaliseUsingLatestQuoteFirstName();
  }, [cognitoUser]);

  return (
    <div className={className}>
      <div className={`${commonPageStylesIdentifier}__hideOnDesktop`}>
        <MobileBackButton history={history} />
      </div>
      <ProgressBar stage={JOURNEY_ABOUT_YOU} />
      <div className={`${commonPageStylesIdentifier}__pageTitle`}>
        <TitleWithUnderLine>Get Quotes</TitleWithUnderLine>
      </div>
      <div className={commonClass('__hideOnDesktop')}>
        <AboutYouBanner />
      </div>
      <div className={`${className}__subHeading`}>
        <Title type="h4" align="left" variant="black">
          Please complete the following (all fields are mandatory unless stated otherwise and should not be left blank):
        </Title>
      </div>
      <div className={`${className}__getQuoteAccordions`}>
        <div>
          <div className={`${className}__accordions`}>
            <AccordionCard
              id={`GetQuote__aboutYou`}
              label="About you"
              status={getFormStatus('aboutYou')}
              childContainerClass={`${className}__noPaddingFormContainer`}
              isOnState={true}
            >
              <FormikAboutYouForm
                aboutYouValid={validity.aboutYou}
                formOpened={handleFormOpen}
                validForm={handleFormSubmit}
                setUpdatedAboutYouValues={setUpdatedAboutYouValues}
                aboutYouValues={aboutYouObj}
                setAboutYouObjForMultipleUpdatedFields={
                  setAboutYouObjForMultipleUpdatedFields
                }
                setAboutYouObjForUpdatedFields={setAboutYouObjForUpdatedFields}
                isLoggedIn={!!cognitoUser}
                aboutYouFormTrackCount={aboutYouFormTrackCount}
                prepopulationStatus={prepopulationStatus}
                loadAgentFormValues={loadAgentFormValues}
                emailParam={emailParam}
                cognitoUser={cognitoUser}
                triggerSubmitButton={triggerSubmitButton}
                townAndCountyData={townAndCountyData}
              />
            </AccordionCard>
          </div>
          {renderSubmitButton()}
        </div>
        <div className={commonClass('__showOnDesktopOnly')}>
          <AboutYouBanner />
        </div>
      </div>
      <div className={`${className}__legalTextContainer`}>
        <LegalText insuranceType="home" />
      </div>
    </div>
  );
};

AboutYouComponent.propTypes = {
  latestQuote: PropTypes.any,
  history: PropTypes.any
};

export default AboutYouComponent;
